<script setup lang="ts">
import type { EditorialGalleryHoverProps } from './EditorialGalleryHover.props'
import emblaCarouselVue from 'embla-carousel-vue'

defineProps<EditorialGalleryHoverProps>()

const rotatedCardIndex = ref<number | null>(null)

const [emblaRef] = emblaCarouselVue({ loop: false })

const toggleFlipperCard = (index: number) => {
  if (rotatedCardIndex.value === index) {
    rotatedCardIndex.value = null
  } else {
    rotatedCardIndex.value = index
  }
}
</script>

<template>
  <div
    class="editorial-gallery-hover relative flex flex-col"
    :class="[
      bgColor ? `bg-neutral-${bgColor}` : 'bg-background-secondary',
      textColor ? `text-editorial-${textColor}` : 'text-text-primary',
    ]"
  >
    <div
      v-if="title || subtitle"
      class="gap-sm px-sm py-xl lg:px-4xl flex flex-col text-center"
    >
      <UtilsMarkdown
        v-if="title"
        :content="title"
        is-preprocessed
        container="span"
        class="text-book-4 lg:text-book-3 uppercase"
      />
      <UtilsMarkdown v-if="subtitle" class="text-light-6" :content="subtitle" />
    </div>
    <div ref="emblaRef" class="embla overflow-hidden">
      <div
        class="embla__container flex cursor-grab select-none gap-[0.5px]"
        grab-cursor
      >
        <div
          v-for="(card, index) in carouselCard"
          :key="`${card.chefName}-${index}`"
          class="embla__slide w-2/3 shrink-0 md:w-2/5 lg:w-[28%]"
        >
          <div class="flex flex-col">
            <div
              class="card relative"
              :class="{
                'has-hover-content': card.hoverElement,
                'is-mobile-flipped':
                  card.hoverElement && rotatedCardIndex === index,
              }"
            >
              <div class="card-side front relative">
                <UIMedia v-bind="card.chefMedia" />

                <button
                  v-if="card.hoverElement && rotatedCardIndex !== index"
                  :aria-label="
                    $ts(
                      'accessibility.editorialModules.galleryHover.flipBackCard'
                    )
                  "
                  class="absolute right-4 top-4 block lg:pointer-events-none lg:opacity-0 lg:focus-within:opacity-100"
                  @click="toggleFlipperCard(index)"
                >
                  <DefaultIconsInfoPlus
                    aria-hidden="true"
                    class="h-6 w-6"
                    :class="`text-editorial-${iconPlusColor}`"
                  />
                </button>
              </div>
              <div
                class="card-side back relative"
                :class="`aspect-${card.chefMedia.aspectRatio}`"
              >
                <div
                  class="lg-custom:px-12 lg-custom:pb-10 flex h-full flex-col items-center justify-end px-6 pb-4 pt-10 md:px-8"
                >
                  <button
                    v-if="card.hoverElement && rotatedCardIndex === index"
                    :aria-label="
                      $ts(
                        'accessibility.editorialModules.galleryHover.flipFrontCard'
                      )
                    "
                    class="absolute right-4 top-4 block lg:pointer-events-none lg:opacity-0 lg:focus-within:opacity-100"
                    @click="toggleFlipperCard(index)"
                  >
                    <DefaultIconsInfoMinus aria-hidden="true" class="h-6 w-6" />
                  </button>

                  <div class="flex flex-grow items-center overflow-auto">
                    <UtilsMarkdown
                      v-if="card.hoverElement?.description"
                      :content="card.hoverElement.description"
                      class="text-book-8 md:text-book-7 lg:text-book-6 text-center"
                    />
                  </div>

                  <div v-if="card.hoverElement?.logo" class="mt-4 shrink-0">
                    <NuxtPicture
                      provider="cloudinary"
                      :src="card.hoverElement.logo?.src"
                      :alt="card.hoverElement.logo?.alt"
                      class="lg-custom:w-32 block w-20 md:w-24"
                      sizes="md:96px lg:128px 80px"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              class="bottom-container px-xs py-sm md:py-md lg:py-lg gap-xs flex flex-col items-center"
            >
              <UtilsMarkdown
                :content="card.chefName"
                is-preprocessed
                container="p"
                class="text-medium-6 md:text-medium-5 uppercase"
              />

              <UILink
                v-bind="card.link"
                class="text-link-7 md:text-link-6 text-center uppercase"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card {
  display: grid;
  place-items: center;
  perspective: 150rem;
  transform-style: preserve-3d;

  &-side {
    backface-visibility: hidden;
    grid-area: 1 / 1;
    transition: transform 0.8s ease;
  }

  &.has-hover-content {
    .back {
      transform: rotateY(180deg);
    }
  }

  &.is-mobile-flipped {
    .front {
      transform: rotateY(180deg);
    }

    .back {
      transform: rotateY(0);
    }
  }
}

// Hover effect for desktop
@media (min-width: 1024px) {
  .card.has-hover-content:hover .front {
    transform: rotateY(180deg);
  }

  .card.has-hover-content:hover .back {
    transform: rotateY(0);
  }
}
</style>
